import { AuthProvider } from '../../../../web/models/authProvider';
import { DeviceRelatedThemeList, GetCurrentSubFeatureNames, VSBConsumerAlertFeature } from '../../../../web/models/constants/getHelpConstants';
import { appContext } from '../../../../web/models/getHelp/appContext';
import { EmeraldUserType } from '../../../../web/models/getHelp/emeraldUserType';
import { ExperienceMetaData } from '../../../../web/models/getHelp/experienceConfigurations';
import { AgeBracket, AutoSuggestResult, UserInfo } from '../../../models/apiModels';
import { Environment, SourceApps } from '../../../models/constants/constants';
import {
  createSelector,
  IAppState,
  IAutoSuggestState,
  IContactUsState,
  IGlobalState,
  IModalitySectionState,
  INavState,
  IOcvState,
  ISelfHelpAlchemyState,
  ISharedState,
  IUITabViewInfo,
  ModalitySectionState,
  ServiceRequestPageType,
  UITabView,
  UITabViewMode,
} from '../../../models/reduxState';
import { IsInAgeBracket } from '../../../utilities';
import { NonAgentModality, NonAgentModalityType } from '../../contactUs/models/modalityModels';
import { ExtensionAttributeKeys } from '../../contactUs/models/requestModels';
import { getGetHelpTheme } from '../../contactUs/styles/themes';
import { getOutlookDotComTheme, getSurfaceAppTheme, getTeamsTheme, getToggleInAppTheme, getWACTheme, inAppThemes, outlookDotComThemes, surfaceAppThemes, teamsThemes, toggleDefaultTheme } from '../../contactUs/styles/themesUI';

export const isThirdPartySelector = (state: IAppState) => {
  return state.GlobalState.environment === Environment.ThirdParty;
};

const checkCurrentUITabView = (
  currentUITabViewInfo: IUITabViewInfo,
  uITabView: UITabView,
  viewMode?: UITabViewMode
): boolean => {
  const currentUITabView = currentUITabViewInfo.uITabView;
  const currentViewMode = currentUITabViewInfo.viewMode;
  const matchesView = currentUITabView === uITabView;
  const viewModeAllowed = !!viewMode ? viewMode === currentViewMode : true;
  return matchesView && viewModeAllowed;
};

export const contactStateSelector = createSelector(
  (state: IAppState) => state.ContactState,
  (c: IContactUsState) => c
);

export const selfHelpAlchemyStateSelector = createSelector(
  contactStateSelector,
  (c: IContactUsState) => c.SelfHelpAlchemyState
);

export const showModalitiesStickyFooterBRSSelector = createSelector(
  selfHelpAlchemyStateSelector,
  (s: ISelfHelpAlchemyState) => s.showModalitiesStickyFooter
);

export const modalitySectionStateSelector = createSelector(
  contactStateSelector,
  (c: IContactUsState) => c.ModalitySectionState
);

export const ocvStateSelector = createSelector(
  contactStateSelector,
  (c: IContactUsState) => c.OcvState
);

export const sharedStateSelector = createSelector(
  contactStateSelector,
  (c: IContactUsState) => c.SharedState
);

export const dafStateSelector = createSelector(
  contactStateSelector,
  (c: IContactUsState) => c.DafState
);

export const autoSuggestStateSelector = createSelector(
  contactStateSelector,
  (c: IContactUsState) => c.AutoSuggestState
);

export const isBusinessAssistUXEnabledSelector = createSelector(
  sharedStateSelector,
  (s: ISharedState) => s.isBusinessAssistUXEnabled
);

export const globalStateSelector = createSelector(
  (state: IAppState) => state.GlobalState,
  (g: IGlobalState) => g
);

export const environmentSelector = createSelector(
  globalStateSelector,
  (g: IGlobalState) => g.environment
);

export const marketSelector = createSelector(
  globalStateSelector,
  (g: IGlobalState) => g.market
);

export const sourceAppSelector = createSelector(
  globalStateSelector,
  (g: IGlobalState) => g.sourceApp
);

export const appVerSelector = createSelector(
  globalStateSelector,
  (g: IGlobalState) => g.appVer
);

export const themeSelector = createSelector(
  globalStateSelector,
  (g: IGlobalState) => g.theme
);

export const navStateSelector = createSelector(
  (state: IAppState) => state.NavState,
  (n: INavState) => n
);

export const getCurrentTabViewInfo = (
  navState: INavState,
): IUITabViewInfo | null => {
  const uITabViewStack = navState.uITabViewStack;
  return uITabViewStack && uITabViewStack.length > 0
    ? uITabViewStack[uITabViewStack.length - 1]
    : null;
};

export const currentUITabViewInfoSelector = createSelector(
  [navStateSelector],
  getCurrentTabViewInfo
);

export const currentUITabViewSelector = createSelector(
  currentUITabViewInfoSelector,
  (uITabViewInfo: IUITabViewInfo) => !!uITabViewInfo ? uITabViewInfo.uITabView : null
);

export const currentViewModeSelector = createSelector(
  currentUITabViewInfoSelector,
  (currentUIViewInfo: IUITabViewInfo) =>
    !!currentUIViewInfo ? currentUIViewInfo.viewMode : null
);

export const getCorrelationId = (state: IAppState): string => {
  const results = state.ContactState.SelfHelpAlchemyState.alchemyResults;
  return results ? results.CorrelationId : '';
};

export const getCurrentServiceRequestPageType = (modalitySectionState: ModalitySectionState): ServiceRequestPageType => {
  const pages = modalitySectionState.serviceRequestPages;
  return (pages && pages.length > 0) ? pages[pages.length - 1].serviceRequestPageType : null;
};

export const isSupportTabEnabled = (state: IAppState) => {
  const currentUITabView = currentUITabViewSelector(state);
  const stickyFooterEnabled =
    state.ContactState.SelfHelpAlchemyState.showModalitiesStickyFooter;
  const userSearched =
    !!state.ContactState.ModalitySectionState.draftRequest &&
    !!state.ContactState.ModalitySectionState.draftRequest.UserDescription;
  // we show ContactSupport immediately after ticket creation, but then hide it as soon as the user selects another tab
  const postRequestCreation =
    currentUITabView === UITabView.requestCreation &&
    getCurrentServiceRequestPageType(state.ContactState.ModalitySectionState) === ServiceRequestPageType.RequestConfirmation;
  const isSignedIn = state.ContactState.ModalitySectionState.isSignedIn;
  return (
    isSignedIn && stickyFooterEnabled && (userSearched || postRequestCreation)
  );
};

export const insightsReturnedSelector = (state: IAppState): boolean => {
  const alchemyResults = state.ContactState.SelfHelpAlchemyState.alchemyResults;
  return (
    !!alchemyResults &&
    !!alchemyResults.Insights &&
    alchemyResults.Insights.length > 0
  );
};

export const isJumpToFirstLinkEnabledSelector = (state: IAppState): boolean => {
  return state.ContactState.SelfHelpAlchemyState.isJumpToFirstLinkEnabled;
}

export const showAutoSuggestCardSelector = (state: IAppState): boolean => {
  const showAutoSuggestCardBRS =
    state.ContactState.SelfHelpAlchemyState.showAutoSuggestCard;
  const alchemySolutionsEnabled =
    state.ContactState.SelfHelpAlchemyState.isAlchmeySolutionsEnabled;
  const isAutoSuggestSearch =
    state.ContactState.AutoSuggestState.isAutoSuggestQuery;
  const autoSuggestCardResults =
    state.ContactState.AutoSuggestState.autoSuggestCardResults;
  const autoSuggestCardResultsV2 =
    state.ContactState.AutoSuggestState.autoSuggestCardResultsV2;
  const autoSuggestReturned =
    !!autoSuggestCardResults && autoSuggestCardResults.length > 0;
  const autoSuggestReturnedV2 =
    !!autoSuggestCardResultsV2 && autoSuggestCardResultsV2.suggestions?.length > 0;
  const contactSearchedQuery =
    state.ContactState.SharedState.contactSearchedQuery || '';
  const hasAutoSuggestsToShow = state.ContactState.AutoSuggestState.isAutoSuggestV2Enabled
    ? autoSuggestReturnedV2 &&
    !!autoSuggestCardResultsV2.suggestions?.find(
      (suggestion: string) =>
        !!suggestion &&
        contactSearchedQuery.toLowerCase() !== suggestion.toLowerCase()
    )
    : autoSuggestReturned &&
    !!autoSuggestCardResults.find(
      (value: AutoSuggestResult) =>
        !!value.title &&
        contactSearchedQuery.toLowerCase() !== value.title.toLowerCase()
    );
  const isAlchemyQueryInProgress =
    state.ContactState.SelfHelpAlchemyState.isAlchemyQueryInProgress;
  /**
   * We show AutoSuggestCard if the user doesn't click on an autoSuggest option.
   * If insights are not returned we show the 'Did you mean section',
   * if insights are returned and showRelatedSearchesBRS == true we show Related searches section
   */
  return (
    showAutoSuggestCardBRS &&
    alchemySolutionsEnabled &&
    hasAutoSuggestsToShow &&
    !isAlchemyQueryInProgress &&
    !isAutoSuggestSearch
  );
};

export const getSuggestionsSelector = (state: IAppState) => {
  const suggestions = state.ContactState.AutoSuggestState.isAutoSuggestV2Enabled
    ? state.ContactState.AutoSuggestState.autoSuggestResultsV2?.suggestions
    : (state.ContactState.AutoSuggestState.isAutoSuggestEnabled
      ? state.ContactState.AutoSuggestState.autoSuggestResults
      : []);

  return suggestions;
};

export const bcqrExperimentTypeSelector = createSelector(
  selfHelpAlchemyStateSelector,
  (s: ISelfHelpAlchemyState) => s.bcqrExperimentType
);

export const authProviderSelector = createSelector(
  modalitySectionStateSelector,
  (c: IModalitySectionState) =>
    !!c.isSignedIn &&
    !!c.userInfo &&
    !!c.userInfo.Provider &&
    AuthProvider[c.userInfo.Provider]
);

export const isSignedInSelector = createSelector(
  modalitySectionStateSelector,
  (modalitySectionState: IModalitySectionState) =>
    modalitySectionState.isSignedIn
);

export const isMsaSelector = createSelector(
  [authProviderSelector, isSignedInSelector],
  (a: AuthProvider, isSignedIn: boolean) =>
    isSignedIn && a === AuthProvider.WindowsLiveId
);

export const isMsaOrUnauthSelector = createSelector(
  [isMsaSelector, isSignedInSelector],
  (isMsa: boolean, isSignedIn: boolean) =>
    !isSignedIn || isMsa
)

export const isAadSelector = createSelector(
  [authProviderSelector, isSignedInSelector],
  (a: AuthProvider, isSignedIn: boolean) =>
    isSignedIn && a === AuthProvider.OrgId
);

export const isAadOrUnauthSelector = createSelector(
  [isAadSelector, isSignedInSelector],
  (isAAD: boolean, isSignedIn: boolean) =>
    !isSignedIn || isAAD
);

export const userInfoSelector = createSelector(
  modalitySectionStateSelector,
  (modalitySectionState: IModalitySectionState) =>
    modalitySectionState.userInfo
);

export const isMsaAdultSelector = createSelector(
  [isMsaSelector, isSignedInSelector, userInfoSelector],
  (isMsa: boolean, isSignedIn: boolean, userInfo: UserInfo) =>
    isMsa && IsInAgeBracket(userInfo, AgeBracket.Adult, isSignedIn)
);

export const isOcvEnabledSelector = createSelector(
  [ocvStateSelector, isMsaAdultSelector],
  (ocvState: IOcvState, isMsaAdult: boolean) => (ocvState.IsEnabled || ocvState.IsOcvVNextEnabled) && isMsaAdult);

export const shouldInitializeOcvSelector = createSelector(
  [ocvStateSelector, isMsaAdultSelector],
  (ocvState: IOcvState, isMsaAdult: boolean) => ocvState.IsEnabled && isMsaAdult && !ocvState.IsInitialized
);

export const isVLastInitializedSelector = createSelector(
  [ocvStateSelector],
  (ocvState: IOcvState) => ocvState.IsInitialized
);

export const shouldSendAimIdSelector = createSelector(
  [ocvStateSelector],
  (ocvState: IOcvState) => ocvState.IsSendAimIdEnabled
);

export const getAimIdSelector = createSelector(
  [
    modalitySectionStateSelector,
  ],
  (
    modalityState: IModalitySectionState,
  ) =>
  {
    return modalityState.draftRequest.ExtensionAttributes[ExtensionAttributeKeys.AimId]
  }
);

export const openLinksInNewTabSelector = createSelector(
  sharedStateSelector,
  (s: ISharedState) => s.openLinksInNewTab);

export const isAgaveSelector = createSelector(
  environmentSelector,
  (e: Environment) => e === Environment.agavePane
);

export const isNonAgaveSelector = createSelector(
  environmentSelector,
  (e: Environment) => e === Environment.nonAgavePane
);

export const isSignUpSelector = createSelector(
  sourceAppSelector,
  (sourceApp: string) => sourceApp === SourceApps.SignUp
);

export const isW365CSelector = createSelector(
  sourceAppSelector,
  (sourceApp: string) => sourceApp === SourceApps.W365C1
);

export const isTeamsSelector = createSelector(
  sourceAppSelector,
  (sourceApp: string) =>
    sourceApp === SourceApps.Teams ||
    sourceApp === SourceApps.Teams2 ||
    sourceApp === SourceApps.TeamsAdmin ||
    sourceApp === SourceApps.TeamsAdmin2
);

export const isSurfaceAppSelector = createSelector(
  sourceAppSelector,
  (sourceApp: string) => 
    sourceApp === SourceApps.SurfaceApp  ||
    sourceApp === SourceApps.SurfaceApp2
);

export const showSearchBarSelector = createSelector(
  [isTeamsSelector, selfHelpAlchemyStateSelector, isNonAgaveSelector, isSignUpSelector],
  (isTeams: boolean, selfHelpAlchemyState: ISelfHelpAlchemyState, isNonAgave: boolean, isSignUp: boolean) =>
    selfHelpAlchemyState.showModalitiesStickyFooter || isTeams || isNonAgave || isSignUp
);

export const showCompactQueryFormSelector = createSelector(
  [isSignUpSelector, showSearchBarSelector],
  (isSignUp: boolean, showSearchBar: boolean) => isSignUp || showSearchBar
);

export const isRequestDetailsSelector = createSelector(
  [currentUITabViewInfoSelector],
  (currentUITabViewInfo: IUITabViewInfo) =>
    checkCurrentUITabView(
      currentUITabViewInfo,
      UITabView.requestDetails
    )
);

export const isRequestCreationSelector = createSelector(
  [currentUITabViewInfoSelector],
  (currentUITabViewInfo: IUITabViewInfo) =>
    checkCurrentUITabView(
      currentUITabViewInfo,
      UITabView.requestCreation
    )
);

export const showSectionHeaderSelector = createSelector(
  [selfHelpAlchemyStateSelector, isNonAgaveSelector],
  (selfHelpAlchemyState: ISelfHelpAlchemyState, isNonAgave: boolean) =>
    selfHelpAlchemyState.showModalitiesStickyFooter || isNonAgave
);

export const isBATenantSelector = createSelector(
  userInfoSelector,
  (userInfo: UserInfo) =>
    userInfo?.IsBATenant
);

export const isVsbUpsellAlertDismissedSelector = createSelector(
  [selfHelpAlchemyStateSelector],
  (selfHelpAlchemyState: ISelfHelpAlchemyState) => selfHelpAlchemyState.isVsbUpsellAlertDismissed
);

export const isVsbUpsellAlertCTAClickedSelector = createSelector(
  [selfHelpAlchemyStateSelector],
  (selfHelpAlchemyState: ISelfHelpAlchemyState) => selfHelpAlchemyState.isVsbUpsellAlertCTAClicked
);

export const compareM365PlansUrlSelector = createSelector(
  [sharedStateSelector],
  (sharedState: ISharedState) => sharedState.compareM365PlansUrl
);

export const isQueryChangedSelector = createSelector(
  [selfHelpAlchemyStateSelector],
  (selfHelpAlchemyState: ISelfHelpAlchemyState) => selfHelpAlchemyState.isQueryChanged
);

export const originalQuerySelector = createSelector(
  [autoSuggestStateSelector],
  (autoSuggestState: IAutoSuggestState) => autoSuggestState.originalQuery
);

export const isDarkThemeSelector = createSelector(
  themeSelector,
  (theme: string) => {
    let isDarkTheme = false;
    if (theme) {
      switch (theme.toLocaleLowerCase()) {
        case 'fluentdarkgray':
        case 'fluentblack':
        case 'darkgray':
        case 'black':
        case 'highcontrastblack':
        case 'dark':
          isDarkTheme = true;
          break;
        default:
          isDarkTheme = false;
          break;
      }
    }
    return isDarkTheme;
  }
);

export const accentColorSelector = createSelector(globalStateSelector, (g: IGlobalState) => g.accentColor || '');

export const getThemeSelector = createSelector(
  [themeSelector, sourceAppSelector, environmentSelector, accentColorSelector],
  (theme: string, sourceApp: SourceApps, environment: Environment, accentColor: string) => {
    let contactUsTheme = toggleDefaultTheme;

    const themeLoaded = isThemeLoaded(theme, sourceApp, environment);

    if (themeLoaded) {
      const themeName = theme;
      if (sourceApp) {
        switch (sourceApp.toLowerCase()) {
          case SourceApps.Teams:
          case SourceApps.Teams2:
          case SourceApps.TeamsAdmin:
          case SourceApps.TeamsAdmin2:
            contactUsTheme = getTeamsTheme(themeName);
            break;
          case SourceApps.SurfaceApp:
          case SourceApps.SurfaceApp2:
            contactUsTheme = getSurfaceAppTheme(themeName);
            break;
          default:
            break;
        }
      }
      switch (environment) {
        case Environment.nonAgavePane:
          contactUsTheme = getToggleInAppTheme(themeName);
          break;
        case Environment.OutlookDotCom:
          contactUsTheme = getOutlookDotComTheme(themeName);
          break;
        case Environment.agavePane:
          contactUsTheme = getWACTheme(themeName);
          break;
        case Environment.GetHelp:
          contactUsTheme = getGetHelpTheme(themeName, accentColor);
          break;
        default:
          break;
      }
    }
    return contactUsTheme;
  }
);

// Gets the Omnichannel theme name that will be sent to Omnichannel Live Chat Widget via below URL:
// https://support.microsoft.com/{0}/home/livechatwidget?theme={1}&customcontext={2}
// Todo: Brook  - check theme support for omnichanel gethelp
export const omnichannelThemeNameSelector = createSelector(
  [themeSelector, sourceAppSelector, environmentSelector],
  (theme: string, sourceApp: SourceApps, environment: Environment) => {
    const emeraldThemeName = !!theme ? theme.toLowerCase() : '';
    let entryPoint = 'smc';
    let themeName = 'colorful';
    const themeLoaded = isThemeLoaded(theme, sourceApp, environment);

    if (themeLoaded) {
      if (sourceApp) {
        switch (sourceApp.toLowerCase()) {
          case SourceApps.Teams:
          case SourceApps.Teams2:
          case SourceApps.TeamsAdmin:
          case SourceApps.TeamsAdmin2:
            entryPoint = 'teams';
            // If emeraldThemeName is valid (member of teamsThemes), use emeraldThemeName,
            // otherwise use default one 'colorful'.
            themeName = !!teamsThemes[emeraldThemeName] ? emeraldThemeName : 'colorful';
            break;
          case SourceApps.SurfaceApp:
          case SourceApps.SurfaceApp2:
            entryPoint = 'surfaceapp';
            themeName = !!surfaceAppThemes[emeraldThemeName] ? emeraldThemeName : 'white';
            break;
          default:
            break;
        }
      }
      switch (environment) {
        case Environment.nonAgavePane:
          entryPoint = 'inapp';
          themeName = !!inAppThemes[emeraldThemeName] ? emeraldThemeName : 'default';
          break;
        case Environment.OutlookDotCom:
          entryPoint = 'outlookdotcom';
          themeName = !!outlookDotComThemes[emeraldThemeName] ? emeraldThemeName : 'fluent';
          break;
        case Environment.agavePane:
          // WAC does not support theming and uses SMC colorful theme for now.
          break;
        default:
          break;
      }
    }

    // Omnichannel theme name format: `${entryPoint}_${themeName}`.
    // Example: 'smc_colorful', 'inapp_darkgray'.
    return `${entryPoint}_${themeName}`;
  }
);

// Check if theme value is set, if expected from source app or environment
const isThemeLoaded = (theme: string, sourceApp: SourceApps, environment: Environment) => {
  let themeLoaded = false;

  if (sourceApp === SourceApps.Teams ||
    sourceApp === SourceApps.Teams2 ||
    sourceApp === SourceApps.TeamsAdmin ||
    sourceApp === SourceApps.TeamsAdmin2 ||
    sourceApp === SourceApps.SurfaceApp ||
    sourceApp === SourceApps.SurfaceApp2 ||
    environment === Environment.agavePane ||
    environment === Environment.nonAgavePane ||
    environment === Environment.OutlookDotCom) {
    themeLoaded = !!theme;
  } else {
    themeLoaded = true;
  }

  return themeLoaded;
};

export const isAmcSelector = createSelector(
  sourceAppSelector,
  (s: SourceApps) => s === SourceApps.AMC
);

export const isSmbHubSelector = createSelector(
  sourceAppSelector,
  (s: SourceApps) => s === SourceApps.SmbHub1
);

const redirectForSignInLinkEnabled = (environment: Environment) => {
  const disabledEnvironments = [Environment.GetHelp];
  return !disabledEnvironments.includes(environment);
};

export const redirectForSignInLinkEnabledSelector = createSelector(environmentSelector, redirectForSignInLinkEnabled);

export const isGetHelpSelector = createSelector(environmentSelector, (e: Environment) => e === Environment.GetHelp);

export const getHelpConfigSelector = createSelector(globalStateSelector, (g: IGlobalState) => g.getHelpConfig);

export const featureFlightListSelector = createSelector(getHelpConfigSelector, (data: ExperienceMetaData) => data?.FeatureFlightSelectedList);

export const showGetCurrentBoxSelector = createSelector(
  [
    selfHelpAlchemyStateSelector,
    isGetHelpSelector,
    featureFlightListSelector,
  ],
  (
    alchemyState: ISelfHelpAlchemyState,
    isGetHelp: boolean,
    featureFlightSelectedList: string[]
  ) =>
    isGetHelp &&
    appContext.emeraldUserType === EmeraldUserType.Consumer &&
    DeviceRelatedThemeList.includes(alchemyState.alchemyResults?.Theme?.Name) &&
    (featureFlightSelectedList?.includes(GetCurrentSubFeatureNames.AfterSearch) || featureFlightSelectedList?.includes(GetCurrentSubFeatureNames.AfterSearchWin11))
);

export const showWindowsUpdateAvailableForSASelfHelp = createSelector(
  [
    sourceAppSelector,
    selfHelpAlchemyStateSelector
  ],
  (
    sourceApps : SourceApps,
    alchemyState: ISelfHelpAlchemyState
  ) =>
  (
    (sourceApps === SourceApps.SurfaceApp || sourceApps === SourceApps.SurfaceApp2) &&
    appContext.emeraldUserType === EmeraldUserType.Consumer &&
    alchemyState.isWindowsUpdateAvailableForSA &&
    DeviceRelatedThemeList.includes(alchemyState.alchemyResults?.Theme?.Name) //to display the Update Alert after search
  )
);

export const showWindowsUpdateAvailableForSAModalities = createSelector(
  [
    sourceAppSelector,
    modalitySectionStateSelector,
  ],
  (
    sourceApps : SourceApps,
    modalityState: IModalitySectionState,
  ) =>
  (
    (sourceApps === SourceApps.SurfaceApp || sourceApps === SourceApps.SurfaceApp2) &&
    appContext.emeraldUserType === EmeraldUserType.Consumer &&
    modalityState.isWindowsUpdateAvailableForSA
  )
);

export const showEndOfLifeMessageForSASelfHelp = createSelector(
  [
    sourceAppSelector,
    selfHelpAlchemyStateSelector
  ],
  (
    sourceApps: SourceApps,
    alchemyState: ISelfHelpAlchemyState
  ) =>
  (
    (sourceApps === SourceApps.SurfaceApp || sourceApps === SourceApps.SurfaceApp2) &&
    appContext.emeraldUserType === EmeraldUserType.Consumer &&
    alchemyState.isEndOfLifeReachedForSA &&
    DeviceRelatedThemeList.includes(alchemyState.alchemyResults?.Theme?.Name) //to display the Update Alert after search
  )
);

export const showEndOfLifeMessageForSAModalities = createSelector(
  [
    sourceAppSelector,
    modalitySectionStateSelector,
  ],
  (
    sourceApps: SourceApps,
    modalityState: IModalitySectionState,
  ) =>
  (
    (sourceApps === SourceApps.SurfaceApp || sourceApps === SourceApps.SurfaceApp2) &&
    appContext.emeraldUserType === EmeraldUserType.Consumer &&
    modalityState.isEndOfLifeReachedForSA
  )
);

export const showWin10EndOfSupportMessage = createSelector(
  [
    sourceAppSelector,
    sharedStateSelector,
  ],
  (sourceApps: SourceApps, sharedState: ISharedState) => (
    sourceApps === SourceApps.GetHelp1 &&
    appContext.emeraldUserType !== EmeraldUserType.Commercial &&
    sharedState.showWin10EndOfSupportMessage
  )
);

const isRoutingDeviceRelated = (extensionAttributes: Record<string, string>): boolean => {
  if (!extensionAttributes) {
    return false;
  }
  const level1 = extensionAttributes[ExtensionAttributeKeys.ThemeL1];
  const level2 = extensionAttributes[ExtensionAttributeKeys.ThemeL2];
  const level3 = extensionAttributes[ExtensionAttributeKeys.ThemeL3];

  const windowsAndOtherProductL2ValidValues = [
    'Password Reset/Recovery',
    'Account sign-in questions',
    'Manage account security',
    'Manage profile information',
    'Devices and drivers',
    'Other accessories',
    'Microsoft Accessories',
    'Time and language settings',
    'Microsoft Accessories',
    'Technical Support',
    'Windows',
    'Devices and drivers',
    'Other accessories',
    'Microsoft Accessories',
  ];

  const surfaceL2ValidValues = [
    'Technical support (e.g. system performance, connectivity)',
    'Hardware problem (e.g. display, battery, audio, keyboard)',
  ];

  const surfaceL3ValidValues = ['Surface Pro', 'Surface Book', 'Surface Go', 'Surface Laptop', 'Surface Studio'];

  if (level1 === 'Windows' || level1 === 'Other Products') {
    return windowsAndOtherProductL2ValidValues.includes(level2);
  }

  if (level1 === 'Surface') {
    return surfaceL2ValidValues.includes(level2) && surfaceL3ValidValues.includes(level3);
  }
  return false;
};

export const showGetCurrentMessageBarBoxOnModalityPage = createSelector(
  [
    modalitySectionStateSelector,
    isGetHelpSelector,
    featureFlightListSelector,
  ],
  (
    modalityState: IModalitySectionState,
    isGetHelp: boolean,
    featureFlightSelectedList
  ) =>
    isGetHelp &&
    appContext.emeraldUserType === EmeraldUserType.Consumer &&
    getCurrentServiceRequestPageType(modalityState) === ServiceRequestPageType.ModalityOptions &&
    (featureFlightSelectedList?.includes(GetCurrentSubFeatureNames.AsModality) || featureFlightSelectedList?.includes(GetCurrentSubFeatureNames.AsModalityWin11)) &&
    isRoutingDeviceRelated(modalityState?.draftRequest?.ExtensionAttributes) &&
    modalityState?.modalityResponse?.NonAgentModalities?.Modalities &&
    modalityState.modalityResponse.NonAgentModalities.Modalities.findIndex(
      (m: NonAgentModality) => m.Type === NonAgentModalityType.Recommendation
    ) !== -1
);

export const recommendationModalitySelector = createSelector(
  [modalitySectionStateSelector, isGetHelpSelector],
  (
    modalityState: IModalitySectionState,
    isGetHelp: boolean
  ) => {
    if (
      isGetHelp &&
      appContext.emeraldUserType === EmeraldUserType.Consumer &&
      getCurrentServiceRequestPageType(modalityState) === ServiceRequestPageType.ModalityOptions
    ) {
      return modalityState.modalityResponse?.NonAgentModalities?.Modalities?.find(
        (nonAgentModality: NonAgentModality) => nonAgentModality.Type === NonAgentModalityType.Recommendation
      );
    }
    return null;
  }
);

export const showAppBar = createSelector(
  [navStateSelector, isGetHelpSelector],
  (navState: INavState, isGetHelp: boolean) =>
    isGetHelp &&
    (appContext.showAboutAndFeedbackButtons ||
      (!!navState.uITabViewStack &&
        navState.uITabViewStack.length > 1 &&
        navState.uITabViewStack[navState.uITabViewStack.length - 1]?.showAppBarBackButton))
);

export const authInfoSelector = createSelector(modalitySectionStateSelector, (m: IModalitySectionState) => m && m.authInfo ? m.authInfo : null);

export const alchemyThemeSelector = createSelector(selfHelpAlchemyStateSelector, (a: ISelfHelpAlchemyState) => a?.alchemyResults?.Theme?.Name);

export const shouldShowVsbUpsellAlertSelector = createSelector(
  [sharedStateSelector, selfHelpAlchemyStateSelector, featureFlightListSelector, isGetHelpSelector],
  (sharedState: ISharedState,
    selfHelpAlchemyState: ISelfHelpAlchemyState,
    featureFlightSelectedList: string[],
    isGetHelp: boolean) =>
    (isGetHelp && appContext.emeraldUserType === EmeraldUserType.Consumer && featureFlightSelectedList?.includes(VSBConsumerAlertFeature) && selfHelpAlchemyState.isVsbUpsellAlertActivated) ||
    (sharedState.shouldShowVsbUpsellAlert && selfHelpAlchemyState.isVsbUpsellAlertActivated)
);

export const shouldResetFocusSelector = createSelector(sharedStateSelector, (sharedState: ISharedState) => sharedState.shouldResetFocus);

export const showMoreInsightsExpandedSelector = createSelector(
  [selfHelpAlchemyStateSelector],
  (selfHelpAlchemyState: ISelfHelpAlchemyState) => selfHelpAlchemyState.showMoreInsightsExpanded
);

export const eduProductIdSelector = (state: IAppState): string => {
  return modalitySectionStateSelector(state).eduProductId;
};

export const isFeedbackCoachmarkEnabledSelector = createSelector(sharedStateSelector, (sharedState: ISharedState) => sharedState.isFeedbackCoachmarkEnabled);

export const isFeedbackCoachmarkShowingSelector = createSelector(sharedStateSelector, (sharedState: ISharedState) => sharedState.isFeedbackCoachmarkShowing);

export const isFeedbackTicketCreationEnabledSelector = createSelector(sharedStateSelector, (sharedState: ISharedState) => sharedState.isFeedbackTicketCreation);

export const isCMCRequestCreationDeeplinkSelector = createSelector(sharedStateSelector, (sharedState: ISharedState) => sharedState.isCMCRequestCreationDeeplink);

export const contactSearchedQuerySelector = createSelector(sharedStateSelector, (sharedState: ISharedState) => sharedState.contactSearchedQuery);

export const isPostAlchemyFeedbackEnabledSelector = createSelector(
  sharedStateSelector,
  (sharedState: ISharedState) => sharedState.isPostAlchemyFeedbackEnabled
);
